import { Injectable } from '@angular/core';
import { ReportKeys } from 'app/main/enums/report-keys.enum';
import moment from 'moment';

export interface ReportFilterState {
  advancedFiltersOpen: boolean;
  gte: string;
  lte: string;
  pageNumber?: number;
  limit: number;
  company_id?: number;
  product_id?: number;
  status?: string;
  user_id?: number;
  page?: number;
  pageSize?: number;
  msisdn?: string;
  is_scheduled?: boolean;
  scheduled_date?: string;
  msgtype?: number;
  destaddr?: string;
  timestamp: number; // to track when the filters were saved
}

@Injectable({
  providedIn: 'root',
})
export class ReportFiltersService {
  // prefix to be used in session storage keys
  private readonly FILTER_PREFIX = 'web2msg_filter_';
  // in-memory cache for quick access
  private filtersCache: Map<ReportKeys, ReportFilterState> = new Map();

  constructor() {}

  // * Save filter state to session storage
  saveFilters(reportKey: ReportKeys, filters: ReportFilterState): void {
    try {
      const dataToSave: ReportFilterState = {
        ...filters,
        timestamp: Date.now(),
      };
      const key = this.getFilterKey(reportKey);
      sessionStorage.setItem(key, JSON.stringify(dataToSave));
      this.filtersCache.set(reportKey, dataToSave);
    } catch (error) {
      console.error('Error saving report filters:', error);
    }
  }

  // * Load filter state from session storage
  loadFilters(reportKey: ReportKeys): ReportFilterState | null {
    try {
      if (this.filtersCache.has(reportKey)) {
        return this.filtersCache.get(reportKey) || null;
      }

      const key = this.getFilterKey(reportKey);
      const storedData = sessionStorage.getItem(key);

      if (!storedData) return null;

      const filters: ReportFilterState = JSON.parse(storedData);
      this.filtersCache.set(reportKey, filters);
      return filters;
    } catch (error) {
      return null;
    }
  }

  // * Clear filter state from session storage
  clearFilters(reportKey: ReportKeys): void {
    try {
      const key = this.getFilterKey(reportKey);
      sessionStorage.removeItem(key);
      this.filtersCache.delete(reportKey);
    } catch (error) {
      console.error('Error clearing report filters:', error);
    }
  }

  // * Convert date string to Date object for flatpickr
  convertDateToFlatpickrFormat(dateStr: string): Date | null {
    return moment(dateStr, ['YYYY-MM-DD', 'DD-MM-YYYY', 'D-M-YYYY']).toDate();
  }

  // * Apply date to flatpickr component
  applyDateToFlatpickr(
    flatpickrComponent: any,
    dateStr: string,
    fieldName: string
  ): void {
    if (!flatpickrComponent || !flatpickrComponent.flatpickr || !dateStr) {
      return;
    }

    try {
      const date = this.convertDateToFlatpickrFormat(dateStr);
      if (date) {
        (flatpickrComponent.flatpickr as any).setDate(date, true);
      }
    } catch (error) {
      console.error(`Error cargando fecha ${fieldName}:`, error);
    }
  }

  // * Restore form values from saved filters (excluding dates)
  restoreFormValues(
    filters: ReportFilterState,
    fieldsToRestore: string[]
  ): any {
    const formValues: any = {};

    fieldsToRestore.forEach((field) => {
      if (filters[field] !== undefined && filters[field] !== null) {
        formValues[field] = filters[field];
      } else {
        formValues[field] = '';
      }
    });

    return formValues;
  }

  // * Get pagination settings from filters
  getPaginationSettings(filters: ReportFilterState): {
    pageSize?: number;
    pageNumber?: number;
  } {
    return {
      pageSize: filters.pageSize,
      pageNumber: filters.pageNumber,
    };
  }

  // * Get advanced filters state
  getAdvancedFiltersState(filters: ReportFilterState): boolean {
    return filters.advancedFiltersOpen || false;
  }

  //* Clear all filters
  clearAllFilters(): void {
    try {
      Object.values(ReportKeys).forEach((reportKey) => {
        this.clearFilters(reportKey);
      });
      this.filtersCache.clear();
    } catch (error) {
      console.error('Error clearing all report filters:', error);
    }
  }

  private getFilterKey(reportKey: ReportKeys): string {
    return `${this.FILTER_PREFIX}${reportKey}`;
  }
}
